import type { JsonSchemaProblem } from '@atlas/core-utils/validations'
import { validateJsonSchema } from '@atlas/core-utils/validations'
import { Button, Textarea } from '@atlas/ui-primitives'
import { useState } from 'react'
import { PromptOutputMode } from '@/domain/prompt.types'

/**
 * What shape a prompt asks the model to produce, and the schema when that is JSON.
 *
 * The mode is DERIVED from whether a schema exists — absent means text — so the two
 * can never contradict each other. Switching to text therefore clears the schema
 * rather than leaving it stored behind an inactive flag, and switching to JSON is not
 * savable until the schema says something: an empty object reads as "structured
 * output" while telling the provider nothing.
 *
 * The draft text is held locally and only parsed on save, because a JSON Schema is
 * invalid for most of the time someone is typing one.
 */
export function PromptOutputEditor({
	schema,
	isSaving,
	onSave,
	onCancel,
}: {
	schema: Record<string, unknown> | null | undefined
	isSaving: boolean
	/** `null` clears the schema, which is how a prompt goes back to text output. */
	onSave: (next: Record<string, unknown> | null) => void
	onCancel: () => void
}) {
	const [mode, setMode] = useState<PromptOutputMode>(schema ? PromptOutputMode.JSON : PromptOutputMode.TEXT)
	const [draft, setDraft] = useState(schema ? JSON.stringify(schema, null, 2) : '')

	const parsed = parseSchema(draft)
	const canSave = mode === PromptOutputMode.TEXT || parsed.ok

	return (
		<div className="space-y-3">
			<fieldset>
				<legend className="mb-1.5 text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Output</legend>
				<div className="flex gap-2">
					{[PromptOutputMode.TEXT, PromptOutputMode.JSON].map((option) => (
						<Button
							key={option}
							size="sm"
							variant={mode === option ? 'secondary' : 'muted'}
							aria-pressed={mode === option}
							onPress={() => setMode(option)}
						>
							{option === PromptOutputMode.TEXT ? 'Text' : 'JSON'}
						</Button>
					))}
				</div>
			</fieldset>

			{mode === PromptOutputMode.TEXT ? (
				<p className="text-ds-sm text-ds-text-muted">Plain prose. Saving clears any schema this prompt carried.</p>
			) : (
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">JSON Schema the model must satisfy</span>
					<Textarea
						rows={10}
						value={draft}
						aria-label="Output schema"
						className="font-mono"
						placeholder={'{\n  "type": "object",\n  "required": ["verdict"]\n}'}
						onChange={(e) => setDraft(e.target.value)}
					/>
					{/*
						Reported as it is typed rather than on save, because a schema is
						invalid for most of the time someone is writing one and a save-time
						rejection loses the place they had reached.
					*/}
					<p className={`mt-1 text-ds-2xs ${parsed.ok ? 'text-ds-text-subtle' : 'text-ds-danger'}`}>
						{parsed.ok ? 'Valid JSON Schema' : parsed.error}
					</p>
				</label>
			)}

			<div className="flex items-center gap-2">
				<Button
					size="sm"
					isDisabled={isSaving || !canSave}
					onPress={() => onSave(mode === PromptOutputMode.TEXT || !parsed.ok ? null : parsed.value)}
				>
					{isSaving ? 'Saving…' : 'Save output'}
				</Button>
				<Button size="sm" variant="muted" isDisabled={isSaving} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</div>
	)
}

/**
 * Parse the draft, refusing what a provider could not act on.
 *
 * An empty object is rejected rather than accepted-and-ignored: it would store "this
 * prompt returns JSON" while describing no structure at all, which is worse than
 * either mode stated plainly.
 */
function parseSchema(text: string): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
	const trimmed = text.trim()
	if (trimmed === '') return { ok: false, error: 'Enter a JSON Schema, or switch to Text output' }

	let value: unknown
	try {
		value = JSON.parse(trimmed)
	} catch (error) {
		return { ok: false, error: error instanceof Error ? error.message : 'Not valid JSON' }
	}

	if (typeof value !== 'object' || value === null || Array.isArray(value)) {
		return { ok: false, error: 'A JSON Schema must be an object' }
	}
	if (Object.keys(value).length === 0) {
		return { ok: false, error: 'An empty object describes nothing — switch to Text output instead' }
	}

	// Structurally checked against draft 2020-12, not merely parsed: a misspelled
	// `type` or a keyword given the wrong shape is valid JSON that the provider then
	// rejects mid-execution. The same helper backs the API's refusal, so the editor
	// cannot accept what a save would reject.
	const problems = validateJsonSchema(value)
	if (problems.length > 0) {
		const first = problems[0] as JsonSchemaProblem
		const more = problems.length > 1 ? ` (+${problems.length - 1} more)` : ''
		return { ok: false, error: `${first.path}: ${first.message}${more}` }
	}

	return { ok: true, value: value as Record<string, unknown> }
}
